import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty, CommandGroup } from '@/components/ui/command';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, Search, X, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api, { setViewAsOrgId, getViewAsOrgId } from '../../utils/api';

/**
 * Global org switcher for super admin impersonation.
 *
 * Renders a combobox that searches organizations via
 * GET /super-admin/organizations/search and starts/stops
 * impersonation sessions via POST /super-admin/impersonate.
 *
 * Only visible to users with internal_role in ['owner', 'super_admin', 'admin'].
 *
 * @param {{ user: object, onImpersonationChange: function }} props
 */
const SuperAdminOrgSwitcher = ({ user, onImpersonationChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeOrg, setActiveOrg] = useState(() => {
    try {
      const stored = sessionStorage.getItem('viewAsOrgInfo');
      return stored ? JSON.parse(stored) : null;
    } catch { return null; }
  });
  const navigate = useNavigate();
  const location = useLocation();
  const debounceRef = useRef(null);
  const [confirmOrg, setConfirmOrg] = useState(null);

  // Allowed internal roles for impersonation
  const ALLOWED_ROLES = ['owner', 'super_admin', 'admin'];
  const canImpersonate = user?.internal_role && ALLOWED_ROLES.includes(user.internal_role);

  const fetchOrgInfo = useCallback(async (orgId) => {
    try {
      const { data } = await api.post(`/super-admin/impersonate/${orgId}`);
      if (data.success) {
        setActiveOrg(data.data);
        setViewAsOrgId(orgId);
        onImpersonationChange?.(data.data);
      }
    } catch (error) {
      console.error('Failed to start impersonation:', error);
      toast.error('Failed to view organization');
    }
  }, [onImpersonationChange]);

  // On mount, check if we're already impersonating via URL param
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewAsOrg = params.get('viewAsOrg');
    if (viewAsOrg && canImpersonate && !activeOrg) {
      setViewAsOrgId(viewAsOrg);
      fetchOrgInfo(viewAsOrg);
    }
  }, [location.search, canImpersonate, activeOrg, fetchOrgInfo]);

  // Debounced search
  const searchOrgs = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await api.get('/super-admin/organizations/search', {
        params: { q, limit: 20 },
      });
      setResults(data.data || []);
    } catch (error) {
      console.error('Org search failed:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (open) searchOrgs(query);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, open, searchOrgs]);

  const handleSelect = (org) => {
    setOpen(false);
    setQuery('');
    setConfirmOrg(org);
  };

  const handleConfirmImpersonate = async () => {
    const org = confirmOrg;
    setConfirmOrg(null);
    if (!org) return;
    try {
      const { data } = await api.post(`/super-admin/impersonate/${org.org_id}`);
      if (data.success) {
        setViewAsOrgId(org.org_id);

        // Update URL and reload so all components re-fetch with new org
        const params = new URLSearchParams(location.search);
        params.set('viewAsOrg', org.org_id);
        window.location.href = `${location.pathname}?${params.toString()}`;
      }
    } catch (error) {
      toast.error('Failed to start impersonation');
    }
  };

  const handleExit = async () => {
    try {
      const orgId = getViewAsOrgId();
      await api.post('/super-admin/impersonate/stop', null, {
        params: { org_id: orgId },
      });
    } catch (error) {
      // Best-effort audit log — don't block exit
      console.error('Failed to log impersonation stop:', error);
    }

    setViewAsOrgId(null);
    sessionStorage.removeItem('viewAsOrgId');
    sessionStorage.removeItem('viewAsOrgInfo');

    // Reload page so all components re-fetch with own org
    const params = new URLSearchParams(location.search);
    params.delete('viewAsOrg');
    const qs = params.toString();
    window.location.href = `${location.pathname}${qs ? '?' + qs : ''}`;
  };

  if (!canImpersonate) return null;

  // Show active org badge when impersonating
  if (activeOrg) {
    return (
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="bg-white/10 border-white/30 text-white text-xs px-2 py-1">
          <Building2 className="w-3 h-3 mr-1" />
          {activeOrg.company_name}
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleExit}
          className="h-6 w-6 p-0 text-white/80 hover:text-white hover:bg-white/10"
          title="Exit impersonation"
        >
          <X className="w-3 h-3" />
        </Button>
      </div>
    );
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1 text-xs text-white/80 hover:text-white hover:bg-white/10"
          title="View as organization"
        >
          <Building2 className="w-3.5 h-3.5" />
          <span className="hidden lg:inline">View as org</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search organizations..."
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>
              {loading ? 'Searching...' : 'No organizations found.'}
            </CommandEmpty>
            <CommandGroup>
              {results.map((org) => (
                <CommandItem
                  key={org.org_id}
                  value={org.org_id}
                  onSelect={() => handleSelect(org)}
                  className="cursor-pointer"
                >
                  <div className="flex items-center justify-between w-full">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{org.company_name}</span>
                      <span className="text-xs text-muted-foreground">{org.owner_email}</span>
                    </div>
                    <Badge variant="secondary" className="text-xs">
                      {org.account_count} accounts
                    </Badge>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>

      {/* Confirmation dialog */}
      <AlertDialog open={!!confirmOrg} onOpenChange={(open) => { if (!open) setConfirmOrg(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              Switch to another organization?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  You are about to view <strong>{confirmOrg?.company_name}</strong> ({confirmOrg?.owner_email}) as if you were logged into their account.
                </p>
                <div className="bg-orange-50 dark:bg-orange-950/50 border border-orange-200 dark:border-orange-800 rounded-md p-3 text-sm text-orange-800 dark:text-orange-200">
                  <strong>Warning:</strong> You will have full read and write access to this organization's data. All actions will be logged in the audit trail.
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmImpersonate}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              View as {confirmOrg?.company_name}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SuperAdminOrgSwitcher;
